import { CELL, GROUND } from '../core/config';
import { MATERIALS, seeded, type City } from './city';

const PALETTES = [
  [0xd6c8ae, 0x665b52, 0x405c68],
  [0xccd4c9, 0x496d68, 0x547f8b],
  [0xbc8068, 0xe5d3b1, 0x425b63],
  [0xdfd9ca, 0x6b7d89, 0x496c7c],
  [0xa7b8b9, 0xede4cd, 0x346773],
  [0xcbb475, 0x595f5e, 0x4d737b],
  [0x9b9ca4, 0xe5d9c3, 0x566875],
  [0xd4b5a7, 0x675c68, 0x4e7178],
];
const ROOFS = [0x9d6049, 0x536470, 0x526b61, 0xb98f66, 0x615961, 0x818d93, 0x774d40, 0x737859];

/** Keep structural floors, doors and service access. Furniture is no longer a
 * physics/render object, and ordinary residents no longer book indoor tasks. */
export function prepareStreetCity(city: City): void {
  city.streetLife = true;
  const buildings = new Map(city.buildings.map((b) => [b.id, b]));
  const furniture = new Set(
    (city.props ?? [])
      .filter(
        (b) =>
          b.kind === 'furniture' &&
          b.parentCell !== undefined &&
          buildings.has(Math.floor(b.parentCell / 1e6)),
      )
      .map((b) => b.id),
  );
  city.props = city.props?.filter((b) => !furniture.has(b.id));
  const interiors = new Map((city.interiors ?? []).map((r) => [r.buildingId, r]));
  for (const room of city.interiors ?? []) room.rooms = undefined;
  for (const place of city.places ?? []) {
    if (place.furnitureId !== undefined && furniture.has(place.furnitureId)) {
      place.furnitureId = undefined;
      place.pose = undefined;
      const b = buildings.get(place.buildingId ?? -1),
        room = interiors.get(place.buildingId ?? -1);
      if (b) place.y = (b.baseY ?? GROUND) + (place.floor * (room?.floorHeight ?? 3) + 1) * CELL + 0.01;
    }
  }
  for (const facility of city.facilities ?? [])
    for (const bed of facility.beds) {
      if (bed.furnitureId !== undefined && furniture.has(bed.furnitureId)) {
        bed.furnitureId = undefined;
        bed.pose = undefined;
      }
    }
  for (const b of city.buildings) {
    if (b.category === 'prop' || b.category === 'bridge') continue;
    const random = seeded(city.seed ^ Math.imul(b.id + 1, 2654435761));
    const palette = Math.floor(random() * PALETTES.length),
      colors = PALETTES[palette];
    const bays = 2 + Math.floor(random() * 4),
      rhythm = Math.floor(random() * 6);
    const roof = Math.floor(random() * 5),
      tint = 0.91 + random() * 0.16;
    const tone = (hex: number) => {
      const r = Math.min(255, Math.round(((hex >> 16) & 255) * tint));
      const g = Math.min(255, Math.round(((hex >> 8) & 255) * tint));
      const blue = Math.min(255, Math.round((hex & 255) * tint));
      return (r << 16) | (g << 8) | blue;
    };
    b.exterior = {
      signature: `${palette}:${bays}:${rhythm}:${roof}:${tone(colors[0]).toString(16)}:${b.id + 1}`,
      addressCell: (
        b.cells
          .filter((c) => c.y === 2)
          .sort((a, c) => a.z - c.z || Math.abs(a.x - 3) - Math.abs(c.x - 3))[0] ?? b.cells[0]
      ).id,
      palette,
      bays,
      rhythm,
      roof,
      roofColor: ROOFS[(b.id * 7 + roof) % ROOFS.length],
      composition: (b.id * 13 + rhythm) % 6,
      trim: colors[1],
      window: colors[2],
    };
    if (!b.exactGeometry) {
      const house = ['pitched-house', 'semi-detached', 'rowhouse', 'barn', 'masonry-highrise'].includes(b.kind);
      for (const cell of b.cells) {
        const outer = cell.x === 0 || cell.z === 0 || cell.x === b.w - 1 || cell.z === b.d - 1;
        // Interior walkways recessed every column, leaving open strips between
        // the external piers and panes. Restore continuous, occupied outer piers.
        if (outer && cell.role === 'column' && cell.y <= (b.roofLevel ?? b.h)) cell.bounds = undefined;
        // The former occupied-house panes no longer need transparent rooms.
        // Restore their original masonry shell and retain the same cell IDs.
        if (house && cell.material === 'glass' && cell.windowTrim !== undefined) {
          cell.material = 'brick';
          cell.role = 'facade';
          cell.hp = MATERIALS.brick.strength;
          cell.bounds = undefined;
          cell.parts = undefined;
          cell.windowTrim = undefined;
        }
        if (b.roofSpan && b.roofLevel !== undefined && cell.role === 'column' && cell.y > b.roofLevel + 1) {
          const rx = cell.x % b.roofSpan;
          const surface = b.roofLevel + 1 + 0.2 +
            Math.min(rx, b.roofSpan - rx, rx + 1, b.roofSpan - rx - 1) * 0.6;
          const bottom = surface - cell.y - 0.02;
          // A chimney starts on the actual slope, not above its containing voxel.
          cell.bounds = { x: 0.2, y: bottom, z: 0.2, w: 0.6, h: 1 - bottom, d: 0.6 };
        }
      }
    }
    if (!b.service && !b.landmark && !b.exactGeometry) {
      b.color = tone(colors[0]);
      b.accent = colors[1];
      for (const cell of b.cells) {
        if (cell.role === 'wall' || cell.role === 'facade' || cell.role === 'column') cell.color = b.color;
        if (cell.material === 'glass') cell.color = colors[2];
        if (cell.windowTrim !== undefined) cell.windowTrim = colors[1];
        // Occupied window frames have their own per-part vertex colours.
        // Recolouring only the parent cell left almost every facade white.
        if (cell.parts && (cell.y % 3 !== 0 || cell.role === 'column'))
          for (const part of cell.parts)
            if (part.color !== undefined) part.color = cell.material === 'glass' ? colors[2] : b.color;
      }
    }
  }
}
